import React from 'react';
import { Link } from 'react-router-dom';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-foreground font-bold text-xl hover:opacity-80 transition-opacity">
            <svg className="w-7 h-7 text-primary" fill="currentColor" viewBox="0 0 24 24">
              <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/>
            </svg>
            TabKeeper
          </Link>
          <Link to="/" className="text-primary hover:text-primary/80 font-medium transition-colors">
            ← Voltar ao início
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-4xl font-extrabold text-foreground mb-2">Termos de Serviço</h1>
        <p className="text-muted-foreground mb-8 pb-8 border-b border-border">
          Última atualização: 21 de fevereiro de 2026
        </p>

        <div className="bg-card rounded-2xl p-8 shadow-sm border border-border space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">1. Aceitação dos Termos</h2>
            <p className="text-foreground/90 mb-4">
              Ao acessar ou usar o TabKeeper ("Serviço"), você concorda em estar vinculado a estes Termos de Serviço. 
              Se você não concordar com qualquer parte destes termos, não poderá acessar o Serviço.
            </p>
            <div className="bg-primary/5 border-l-4 border-primary p-4 rounded-r-lg">
              <strong className="text-foreground">Resumo:</strong>
              <span className="text-foreground/80"> Ao usar o TabKeeper, você concorda com estas regras. 
              O serviço é fornecido para ajudá-lo a organizar seus favoritos de forma eficiente e segura.</span>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">2. Descrição do Serviço</h2>
            <p className="text-foreground/90 mb-3">O TabKeeper é uma plataforma de gerenciamento de favoritos que oferece:</p>
            <ul className="list-disc pl-6 space-y-2 text-foreground/90">
              <li>Armazenamento e organização de bookmarks/favoritos</li>
              <li>Categorização hierárquica com pastas e tags</li>
              <li>Sincronização entre dispositivos</li>
              <li>Importação e exportação de favoritos</li>
              <li>Extensão para navegador Chrome</li>
              <li>Aplicação web progressiva (PWA)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">3. Registro e Conta</h2>
            <p className="text-foreground/90 mb-3">Para utilizar o Serviço, você deve:</p>
            <ul className="list-disc pl-6 space-y-2 text-foreground/90">
              <li>Ter pelo menos 13 anos de idade</li>
              <li>Fornecer informações verdadeiras e completas durante o registro</li>
              <li>Manter a segurança de sua senha e credenciais</li>
              <li>Notificar-nos imediatamente sobre qualquer uso não autorizado</li>
            </ul>
            <p className="text-foreground/90 mt-3">Você é responsável por todas as atividades que ocorrem em sua conta.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">4. Uso Aceitável</h2>
            <p className="text-foreground/90 mb-3">Ao usar o TabKeeper, você concorda em <strong>NÃO</strong>:</p>
            <ul className="list-disc pl-6 space-y-2 text-foreground/90">
              <li>Violar leis ou regulamentos aplicáveis</li>
              <li>Armazenar links para conteúdo ilegal, difamatório ou prejudicial</li>
              <li>Tentar acessar contas de outros usuários</li>
              <li>Interferir ou interromper o funcionamento do Serviço</li>
              <li>Usar automação, bots ou scripts não autorizados</li>
              <li>Realizar engenharia reversa do software</li>
              <li>Revender ou redistribuir o Serviço sem autorização</li>
            </ul>
            <div className="bg-yellow-500/10 border-l-4 border-yellow-500 p-4 rounded-r-lg mt-4">
              <strong className="text-foreground">Atenção:</strong>
              <span className="text-foreground/80"> Violações graves destes termos podem resultar na suspensão 
              ou encerramento imediato de sua conta, sem aviso prévio.</span>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">5. Propriedade Intelectual</h2>
            <p className="text-foreground/90 mb-3">
              <strong>Nosso conteúdo:</strong> O TabKeeper, incluindo seu design, código, logotipos e marcas, 
              são propriedade exclusiva nossa e protegidos por leis de propriedade intelectual.
            </p>
            <p className="text-foreground/90">
              <strong>Seu conteúdo:</strong> Você mantém todos os direitos sobre os favoritos e dados que armazena no Serviço. 
              Ao usar o TabKeeper, você nos concede uma licença limitada para processar e armazenar seus dados apenas para fornecer o Serviço.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">6. Privacidade</h2>
            <p className="text-foreground/90">
              Sua privacidade é importante para nós. O uso de suas informações pessoais é regido por nossa{' '}
              <Link to="/privacidade" className="text-primary hover:underline">Política de Privacidade</Link>, 
              que é incorporada a estes Termos por referência.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">7. Disponibilidade do Serviço</h2>
            <p className="text-foreground/90 mb-3">Nos esforçamos para manter o Serviço disponível 24/7, porém:</p>
            <ul className="list-disc pl-6 space-y-2 text-foreground/90">
              <li>Não garantimos disponibilidade ininterrupta</li>
              <li>Manutenções programadas serão comunicadas com antecedência quando possível</li>
              <li>Não somos responsáveis por interrupções causadas por terceiros</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">8. Limitação de Responsabilidade</h2>
            <p className="text-foreground/90 mb-3">O Serviço é fornecido "como está" e "conforme disponível". Na extensão máxima permitida por lei:</p>
            <ul className="list-disc pl-6 space-y-2 text-foreground/90">
              <li>Não garantimos que o Serviço atenderá todos os seus requisitos</li>
              <li>Não somos responsáveis por perda de dados devido a falhas técnicas</li>
              <li>Recomendamos manter backups regulares de seus favoritos</li>
              <li>Nossa responsabilidade total não excederá o valor pago pelo Serviço nos últimos 12 meses</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">9. Indenização</h2>
            <p className="text-foreground/90 mb-3">Você concorda em indenizar e isentar o TabKeeper de quaisquer reclamações, danos ou despesas decorrentes de:</p>
            <ul className="list-disc pl-6 space-y-2 text-foreground/90">
              <li>Seu uso do Serviço</li>
              <li>Violação destes Termos</li>
              <li>Violação de direitos de terceiros</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">10. Modificações do Serviço</h2>
            <p className="text-foreground/90 mb-3">Reservamos o direito de:</p>
            <ul className="list-disc pl-6 space-y-2 text-foreground/90">
              <li>Modificar ou descontinuar recursos do Serviço</li>
              <li>Atualizar estes Termos a qualquer momento</li>
              <li>Alterar preços com aviso prévio de 30 dias</li>
            </ul>
            <p className="text-foreground/90 mt-3">Alterações significativas serão comunicadas por e-mail ou notificação no aplicativo.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">11. Encerramento</h2>
            <p className="text-foreground/90 mb-3">
              Você pode encerrar sua conta a qualquer momento através das configurações do aplicativo. 
              Nós podemos suspender ou encerrar seu acesso se:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-foreground/90">
              <li>Você violar estes Termos</li>
              <li>Sua conta permanecer inativa por mais de 24 meses</li>
              <li>For necessário por motivos legais ou de segurança</li>
            </ul>
            <p className="text-foreground/90 mt-3">Após o encerramento, você poderá exportar seus dados por um período de 30 dias.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">12. Lei Aplicável</h2>
            <p className="text-foreground/90">
              Estes Termos são regidos pelas leis do Brasil. Quaisquer disputas serão resolvidas nos tribunais 
              competentes do Brasil, sem prejuízo de outros foros que possam ser aplicáveis.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">13. Disposições Gerais</h2>
            <ul className="list-disc pl-6 space-y-2 text-foreground/90">
              <li><strong>Acordo Integral:</strong> Estes Termos constituem o acordo completo entre você e o TabKeeper.</li>
              <li><strong>Renúncia:</strong> A falha em exercer qualquer direito não constitui renúncia.</li>
              <li><strong>Divisibilidade:</strong> Se qualquer disposição for inválida, as demais permanecerão em vigor.</li>
              <li><strong>Cessão:</strong> Você não pode ceder seus direitos sem nosso consentimento.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">14. Contato</h2>
            <p className="text-foreground/90 mb-3">Para questões sobre estes Termos de Serviço:</p>
            <ul className="list-disc pl-6 space-y-2 text-foreground/90">
              <li><strong>E-mail:</strong> suporte@tabkeeper.app</li>
            </ul>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="text-center py-8 text-muted-foreground text-sm">
        <p>© 2026 TabKeeper. Todos os direitos reservados.</p>
        <p className="mt-2">
          <Link to="/privacidade" className="text-primary hover:underline">Privacidade</Link>
          {' · '}
          <Link to="/termos" className="text-primary hover:underline">Termos de Serviço</Link>
        </p>
      </footer>
    </div>
  );
};

export default TermsPage;
